/**
 * Transport configuration.
 *
 * Lives under `api/` rather than `app/` so the transport does not depend on the
 * application layer. Reading config from `app/` would invert the dependency
 * direction and make this module impossible to test in isolation.
 *
 * In production the SPA is served behind a trusted edge/BFF that proxies the API
 * on the SAME ORIGIN. That is why the default is a relative `/api/v1` rather than
 * an absolute URL: a same-origin deployment means no cross-origin cookies and no
 * CORS configuration to keep in sync.
 */

const rawBaseUrl = import.meta.env['VITE_API_BASE_URL'] ?? '/api/v1';

function normaliseBase(raw: string): string {
  const withSlash = raw.endsWith('/') ? raw : `${raw}/`;
  // `new URL` rejects a relative base, so resolve against the document origin
  // when one exists (browser/test) and against a placeholder when it does not.
  const origin = globalThis.location?.origin ?? 'http://localhost';
  return new URL(withSlash, origin).toString();
}

export const env = {
  apiBaseUrl: normaliseBase(rawBaseUrl),

  /**
   * Tenant context hint.
   *
   * A HINT ONLY. The backend revalidates tenant scope on every request and must
   * never treat this header as authorization. It exists to let the server skip a
   * redundant lookup, not to decide anything.
   */
  tenantSlug: import.meta.env['VITE_TENANT_SLUG'] as string | undefined,

  /**
   * Development-only hostname override.
   *
   * Lets the multi-domain resolution logic be exercised on one machine without
   * DNS or TLS. Compiled out of production builds — see `isDevHostOverrideActive`.
   */
  hostOverride: import.meta.env['VITE_HOST_OVERRIDE'] as string | undefined,

  /**
   * Unique identifier stamped into the bundle at build time by `vite.config.ts`.
   *
   * Compared against `/version.json` at runtime so long-lived tabs detect new
   * deployments and reload rather than running stale UI code.
   */
  buildId: (import.meta.env['VITE_BUILD_ID'] as string | undefined) ?? 'dev',
} as const;

export function isDevHostOverrideActive(): boolean {
  return import.meta.env.DEV && env.hostOverride !== undefined;
}
